import { useCallback, useEffect, useRef, useState } from 'react'
import { Tooltip } from '@/components/ui/tooltip'
import { dayjs } from '@/dayjs'
import { cn } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'
import { BAR_PADDING_Y, LANE_HEIGHT, STATUS_STYLES } from '../constants'
import type { PositionedActivity, RoadmapActivity, SideValues } from '../types'

const BAR_CLASS =
	'absolute flex items-center justify-center overflow-hidden rounded-md border px-2 text-center font-medium text-xs leading-tight'

/** The share label a bar shows. Sides with no work are dropped, not shown as 0%. */
const shareLabelOf = (
	share: SideValues,
	t: ReturnType<typeof useTranslation>['t']
): string | undefined => {
	const parts: string[] = []
	if (share.backend > 0) {
		parts.push(
			t('roadmaps:share_backend', {
				value: `${Math.round(share.backend)}%`
			})
		)
	}
	if (share.frontend > 0) {
		parts.push(
			t('roadmaps:share_frontend', {
				value: `${Math.round(share.frontend)}%`
			})
		)
	}
	return parts.length ? parts.join(' · ') : undefined
}

interface GanttActivityBarProps {
	positioned: PositionedActivity
	dayWidth: number
	/** Absolute offset of this bar's lane inside the category row. */
	top: number
	/** Reports the bar's height so the row can stack the lanes below it. */
	onHeightChange: (activityTitle: string, height: number) => void
	/** Opens the activity for editing. Omitted for read-only viewers. */
	onSelect?: (activity: RoadmapActivity) => void
}

function GanttActivityBar({
	positioned,
	dayWidth,
	top,
	onHeightChange,
	onSelect
}: GanttActivityBarProps) {
	const { t } = useTranslation()
	const { activity, offsetUnits, spanUnits } = positioned
	const contentObserverRef = useRef<ResizeObserver | null>(null)
	const [contentHeight, setContentHeight] = useState(0)

	/**
	 * The label's natural height, measured rather than derived: how many lines a
	 * title wraps into depends on font metrics we would only be guessing at. The
	 * label is measured, never the bar: the bar's height is set from this, so
	 * measuring the bar would feed its own output back in.
	 */
	const setContentNode = useCallback((node: HTMLSpanElement | null) => {
		contentObserverRef.current?.disconnect()
		if (!node) {
			return
		}
		setContentHeight(node.offsetHeight)
		const observer = new ResizeObserver(([entry]) => {
			setContentHeight(entry.contentRect.height)
		})
		observer.observe(node)
		contentObserverRef.current = observer
	}, [])

	// Rounded up to a whole number of lanes, so the bar is always at least as
	// tall as its text: a label needing 38px gets 56, never a clipping 28.
	const height =
		Math.max(Math.ceil((contentHeight + BAR_PADDING_Y) / LANE_HEIGHT), 1) *
		LANE_HEIGHT

	useEffect(() => {
		onHeightChange(activity.title, height)
	}, [activity.title, height, onHeightChange])

	// Already a percentage of capacity, computed by the backend: shown as-is.
	const share: SideValues = {
		backend: activity.backendPercentage,
		frontend: activity.frontendPercentage
	}
	const shareLabel = shareLabelOf(share, t)

	const rangeLabel = `${dayjs(activity.dateFrom).format('MMM D')} → ${dayjs(activity.dateTo).format('MMM D, YYYY')}`

	return (
		<Tooltip
			content={
				<div className='flex flex-col gap-1'>
					<span className='font-semibold'>{activity.title}</span>
					<span className='font-normal opacity-80'>{rangeLabel}</span>
					<span className='font-normal opacity-80'>
						{t(STATUS_STYLES[activity.status].label)}
					</span>
					{activity.lead ? (
						<span className='font-normal opacity-80'>
							{t('roadmaps:lead_label', { name: activity.lead })}
						</span>
					) : null}
					{shareLabel ? (
						<span className='font-normal opacity-80'>
							{shareLabel}
						</span>
					) : null}
				</div>
			}
			isOpenOnClickEnabled={false}
			triggerProps={{ asChild: true }}
		>
			{/* A button so the bar is focusable: keyboard users reach the
			    tooltip through focus and, when editing is allowed, open the
			    editor with Enter. Read-only viewers get the tooltip only. */}
			<button
				aria-label={`${activity.title}, ${rangeLabel}`}
				className={cn(
					BAR_CLASS,
					onSelect ? 'cursor-pointer' : 'cursor-default',
					STATUS_STYLES[activity.status].bar
				)}
				onClick={onSelect ? () => onSelect(activity) : undefined}
				style={{
					left: offsetUnits * dayWidth,
					top,
					width: Math.max(spanUnits * dayWidth - 2, 4),
					height
				}}
				type='button'
			>
				{/* Title and share flow as one wrapping paragraph: the bar grows to
				    fit instead of the label being cut. The 4-line clamp is a safety
				    rail, a one-day bar unclamped would wrap a long title into a
				    tower; past it the tooltip carries the full text. */}
				<span className='line-clamp-4 break-words' ref={setContentNode}>
					{activity.title}
					{shareLabel ? (
						<span className='font-semibold opacity-70'>{` ${shareLabel}`}</span>
					) : null}
				</span>
			</button>
		</Tooltip>
	)
}

export { GanttActivityBar }
